<template>
  <div
    v-if="isAccountRecon"
    class="task-financial-metrics d-flex"
    data-testid="task-financial-metrics"
  >
    <div
      v-for="metric in metrics"
      :key="metric.id"
      class="task-financial-metrics__col"
      :data-testid="`task-financial-metrics__col-${metric.id}`"
    >
      <div
        class="task-financial-metrics__card d-flex flex-column gap-2"
        :class="metric.cardClass"
        :data-testid="`task-financial-metrics__card-${metric.id}`"
      >
        <span
          class="metric-label"
          :class="metric.labelClass"
          :data-testid="`task-financial-metrics__label-${metric.id}`"
        >
          {{ metric.label }}
        </span>
        <div
          class="metric-value-row d-flex align-center justify-space-between"
          :data-testid="`task-financial-metrics__value-${metric.id}`"
        >
          <span class="metric-value" :class="metric.valueClass">{{ metric.value }}</span>
          <span
            v-if="metric.subLabel"
            class="task-financial-metrics__sub"
            :class="metric.subLabelClass"
            :data-testid="`task-financial-metrics__sub-${metric.id}`"
          >
            {{ metric.subLabel }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './TaskFinancialMetrics.scss';
import {
  getTbTotals,
  calculateScheduleAmount,
  calculateExplainedDifference,
  calculateUnexplainedDifference,
} from '@/modules/financialclose/utils/utils';
import {
  TASK_FIELD_LABELS,
  TASK_FINANCIAL_METRIC_TEXT,
} from '@/modules/financialclose/utils/listConstants';

export default {
  name: 'TaskFinancialMetrics',

  props: {
    task: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      accountReconType: this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION,
    };
  },

  computed: {
    isAccountRecon() {
      return this.task?.task_type === this.accountReconType;
    },

    unexplainedDiff() {
      return calculateUnexplainedDifference(this.task);
    },

    thresholdDisplay() {
      const v = this.task?.threshold;
      if (v === null || v === undefined || v === '') return TASK_FINANCIAL_METRIC_TEXT.NOT_AVAILABLE;
      return this.formatNumber(v);
    },

    unexplainedThresholdSubLabel() {
      if (this.thresholdDisplay === TASK_FINANCIAL_METRIC_TEXT.NOT_AVAILABLE) return '';
      return `${TASK_FINANCIAL_METRIC_TEXT.THRESHOLD} $${this.thresholdDisplay}`;
    },

    metrics() {
      const unexplained = this.unexplainedDiff;
      const aboveThreshold = Math.abs(Number(unexplained)) > Number(this.task?.threshold ?? 0);
      const unexplainedCardClass = aboveThreshold
        ? 'task-financial-metrics__card--unexplained-red'
        : 'task-financial-metrics__card--unexplained-green';

      return [
        {
          id: 'trial-balance',
          label: TASK_FIELD_LABELS.TRIAL_BALANCE,
          value: this.formatNumber(getTbTotals(this.task)),
          cardClass: 'task-financial-metrics__card--info',
          labelClass: '',
          valueClass: '',
        },
        {
          id: 'recon-amount',
          label: TASK_FIELD_LABELS.RECON_AMOUNT,
          value: this.formatNumber(calculateScheduleAmount(this.task)),
          cardClass: 'task-financial-metrics__card--info',
          labelClass: '',
          valueClass: '',
        },
        {
          id: 'explained-diff',
          label: TASK_FIELD_LABELS.EXPLAINED_DIFFERENCE,
          value: this.formatNumber(calculateExplainedDifference(this.task)),
          cardClass: 'task-financial-metrics__card--info',
          labelClass: '',
          valueClass: '',
        },
        {
          id: 'unexplained-diff',
          label: TASK_FIELD_LABELS.UNEXPLAINED_DIFFERENCE,
          value: this.formatCurrency(unexplained),
          cardClass: unexplainedCardClass,
          labelClass: aboveThreshold ? 'metric-label--red' : 'metric-label--green',
          valueClass: aboveThreshold ? 'metric-value--red' : 'metric-value--green',
          subLabel: this.unexplainedThresholdSubLabel,
          subLabelClass: aboveThreshold ? 'task-financial-metrics__sub--red' : 'task-financial-metrics__sub--green',
        },
      ];
    },
  },

  methods: {
    formatNumber(n) {
      if (typeof n !== 'number' || Number.isNaN(n)) n = Number(n) || 0;
      if (n < 0) {
        return `(${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })})`;
      }
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
    },

    formatCurrency(n) {
      const formatted = this.formatNumber(n);
      if (formatted.startsWith('(')) {
        return `($${formatted.slice(1, -1)})`;
      }
      return `$${formatted}`;
    },
  },
};
</script>
